import { useState } from "react";
import { Eye, EyeOff, Lock } from "lucide-react";

/**
 * PasswordInput — shared text input with show/hide toggle.
 *
 * Wraps a native <input> so it works as a drop-in replacement anywhere
 * `<input type="password" />` is used. Renders an icon on the left (default:
 * Lock) and an Eye/EyeOff toggle on the right.
 *
 * Props mirror native <input> + optional `leftIcon` (React node), `wrapperClassName`,
 * and `testIdSuffix` (used to derive data-testid for the toggle button).
 */
export default function PasswordInput({
  leftIcon,
  wrapperClassName = "",
  className = "",
  testIdSuffix = "password",
  showByDefault = false,
  ...inputProps
}) {
  const [visible, setVisible] = useState(showByDefault);
  const Icon = leftIcon ?? <Lock className="w-4 h-4" />;
  return (
    <div className={`relative ${wrapperClassName}`}>
      {Icon && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none">
          {Icon}
        </span>
      )}
      <input
        {...inputProps}
        type={visible ? "text" : "password"}
        className={`${Icon ? "pl-10" : ""} pr-11 ${className}`}
      />
      <button
        type="button"
        aria-label={visible ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
        data-testid={`toggle-${testIdSuffix}-visibility`}
        onClick={() => setVisible((v) => !v)}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-stone-400 hover:text-charcoal hover:bg-stone-100 transition-colors"
        tabIndex={-1}
      >
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}
